import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Download, FileText, Check } from "lucide-react";
import { trainingSample as content } from "../../content/siteContent";
import { SectionHeading, CTAButton } from "./primitives";
import { Reveal } from "../motion/Reveal";
import { API, useConfig, getUTMParams } from "../../hooks/useConfig";
import { track, EVENTS } from "../../lib/analytics";
import { fireConversion } from "../../lib/conversions";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  "w-full bg-white border border-input rounded-sm px-4 py-3 text-sm text-navy placeholder:text-slate-ink/50 focus:outline-none focus:border-teal focus:ring-1 focus:ring-teal transition-colors duration-200";

export const TrainingSample = () => {
  const config = useConfig();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", work_email: "", company_website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const startedRef = useRef(false);

  const update = (k, v) => {
    if (!startedRef.current) {
      startedRef.current = true;
      track(EVENTS.FORM_STARTED, { form: "sample_material" });
    }
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = "Please enter your first name.";
    if (!form.work_email.trim()) e.work_email = "Please enter your work email.";
    else if (!emailRe.test(form.work_email)) e.work_email = "Please enter a valid email address.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/leads`, {
        first_name: form.first_name,
        last_name: "",
        work_email: form.work_email,
        organisation: "",
        intent: "sample_material",
        company_website: form.company_website,
        ...getUTMParams(),
      });
      setStatus("success");
      track(EVENTS.SAMPLE_REQUESTED);
      track(EVENTS.LEAD_CONVERTED, { intent: "sample_material" });
      fireConversion("lead", {
        user: { email: form.work_email, first_name: form.first_name },
      });
      navigate("/free-sample/thank-you");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      setErrors((e) => ({ ...e, submit: msg }));
      setStatus("error");
    }
  };

  const onDownload = () => track(EVENTS.SAMPLE_DOWNLOADED);

  return (
    <section
      id="training-sample"
      data-testid="training-sample-section"
      className="bg-offwhite py-24 lg:py-32 border-t border-hair"
    >
      <div className="container-page grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left — teaser + preview */}
        <div className="lg:col-span-6">
          <SectionHeading eyebrow={content.eyebrow}>{content.heading}</SectionHeading>
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-ink text-base lg:text-lg leading-relaxed max-w-xl">
              {content.intro}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <ul className="mt-8 space-y-3 max-w-xl">
              {content.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-teal mt-1 shrink-0" />
                  <span className="text-slate-ink text-sm leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.2}>
            <figure className="mt-8 rounded-sm overflow-hidden border border-hair max-w-md bg-white shadow-sm">
              <img
                src={content.image}
                alt="Sample page from the Meeting Management guide — the 5 Ps model"
                className="w-full h-auto object-contain"
                loading="lazy"
              />
              <figcaption className="flex items-center gap-2 px-4 py-3 border-t border-hair bg-offwhite">
                <FileText className="h-4 w-4 text-teal shrink-0" />
                <span className="text-slate-ink text-xs font-display font-semibold tracking-wide">
                  {content.meta}
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>

        {/* Right — short form / download */}
        <div className="lg:col-span-6 lg:col-start-8">
          <div className="bg-navy border border-hair-dark rounded-sm p-7 lg:p-10 relative overflow-hidden">
            <div className="absolute inset-0 blueprint opacity-40" />
            <div className="relative">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="sample-success"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="sample-success"
                    className="text-center py-6"
                  >
                    <CheckCircle2 className="h-11 w-11 text-teal mx-auto" />
                    <h3 className="mt-5 font-display font-bold text-white text-2xl tracking-tight">
                      {content.successTitle}
                    </h3>
                    <p className="mt-3 text-white/70 text-sm leading-relaxed max-w-sm mx-auto">
                      {content.successBody}
                    </p>
                    {config.sample_material_url ? (
                      <div className="mt-7">
                        <CTAButton
                          as="a"
                          href={config.sample_material_url}
                          target="_blank"
                          rel="noopener"
                          data-testid="download-sample"
                          onClick={onDownload}
                          withArrow
                        >
                          <Download className="h-4 w-4" /> {content.downloadCta}
                        </CTAButton>
                      </div>
                    ) : (
                      <p className="mt-6 text-white/50 text-xs">
                        The download link will be sent to your email.
                      </p>
                    )}
                  </motion.div>
                ) : (
                  <motion.div key="sample-form" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <p className="eyebrow text-teal">{content.formTitle}</p>
                    <p className="mt-2 text-white/70 text-sm leading-relaxed">{content.formNote}</p>

                    {/* Honeypot */}
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      value={form.company_website}
                      onChange={(e) => update("company_website", e.target.value)}
                      className="hidden"
                      name="company_website"
                    />

                    <div className="mt-6 space-y-4">
                      <div>
                        <label htmlFor="sample_first_name" className="block text-xs font-display font-semibold text-white/80 mb-1.5">
                          First name <span className="text-teal">*</span>
                        </label>
                        <input
                          id="sample_first_name"
                          data-testid="sample-input-first-name"
                          className={inputCls}
                          value={form.first_name}
                          onChange={(e) => update("first_name", e.target.value)}
                        />
                        {errors.first_name && (
                          <p className="mt-1 text-xs text-[#F0A38F]" role="alert" data-testid="sample-error-first-name">
                            {errors.first_name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="sample_work_email" className="block text-xs font-display font-semibold text-white/80 mb-1.5">
                          Work email <span className="text-teal">*</span>
                        </label>
                        <input
                          id="sample_work_email"
                          type="email"
                          data-testid="sample-input-work-email"
                          className={inputCls}
                          value={form.work_email}
                          onChange={(e) => update("work_email", e.target.value)}
                        />
                        {errors.work_email && (
                          <p className="mt-1 text-xs text-[#F0A38F]" role="alert" data-testid="sample-error-work-email">
                            {errors.work_email}
                          </p>
                        )}
                      </div>

                      {errors.submit && (
                        <p className="text-sm text-[#F0A38F]" role="alert" data-testid="sample-error-submit">
                          {errors.submit}
                        </p>
                      )}

                      <CTAButton
                        className="w-full"
                        data-testid="sample-submit"
                        disabled={status === "loading"}
                        onClick={submit}
                      >
                        {status === "loading" ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Download className="h-4 w-4" /> {content.cta}
                          </>
                        )}
                      </CTAButton>

                      <p className="text-white/40 text-xs leading-relaxed">{content.privacy}</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
